<template>
  <app-alert
    v-if="isDunning"
    :tone="tone"
    :title="title"
    class="m-4 mb-0"
    data-test="dunning-banner"
    :data-billing-state="billingAccountState"
  >
    {{ message }}
    <template #actions>
      <app-button
        tone="primary"
        size="sm"
        data-test="dunning-banner-fix"
        :to="{ name: 'subscription.invoices', query: { billing: billingAccountState } }"
      >
        {{ t('billing.dunning.fix_action') }}
      </app-button>
    </template>
  </app-alert>
</template>

<script setup>
  /**
   * Persistent app-shell banner shown while a tenant's billing is in a
   * dunning state. `past_due`/`unpaid` accounts are also redirected to the
   * billing page by the auth guard, but `grace_period` accounts keep access
   * and roam the app — this banner is their standing reminder to fix payment
   * before the grace deadline hard-blocks them (see the scrumble
   * `billing:expire-grace-periods` sweep).
   */
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';

  import AppAlert from '@/components/ui/feedback/AppAlert.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { useEntitlementsStore } from '@/stores/useEntitlementsStore';

  defineOptions({ name: 'DunningBanner' });

  // Maps a dunning billing state to the alert tone it should render with.
  const DUNNING_TONES = {
    past_due: 'danger',
    unpaid: 'danger',
    grace_period: 'warning',
  };

  const { t } = useI18n();
  const { billingAccountState } = storeToRefs(useEntitlementsStore());

  const isDunning = computed(() => Boolean(DUNNING_TONES[billingAccountState.value]));
  const tone = computed(() => DUNNING_TONES[billingAccountState.value] ?? 'warning');
  const title = computed(() =>
    isDunning.value ? t(`billing.dunning.${billingAccountState.value}.title`) : ''
  );
  const message = computed(() =>
    isDunning.value ? t(`billing.dunning.${billingAccountState.value}.message`) : ''
  );
</script>
